import React, { ReactNode, RefObject } from 'react';
import { WixPatternsTestProvider } from '../components';
import { Page, WixStyleReactEnvironmentProvider } from '@wix/design-system';
import { WixPatternsContainerProvider } from '@wix/bex-core/react';
import { CairoContainerDriver } from '@wix/bex-core/testkit/internal';
import { InfiniteScrollPage } from '../components/PageWrapper';
import { EssentialsProvider } from '@wix/fe-essentials/react';
import { CairoEntityPageState, EntityPageState } from '../state';
import { CairoEntityPage } from '../components/CairoEntityPage';
import { CollectionPageState } from '@wix/bex-core';
import { EntityPage } from '../components/EntityPage';

export interface CairoTestAppProps {
  driver: CairoContainerDriver;
  children?: ReactNode;
  page?: 'none' | 'wsr' | 'entity' | 'entity-page';
  scrollElement?: RefObject<HTMLDivElement>;
  pageState?: CairoEntityPageState;
  entityPageState?: EntityPageState<any>;
  collectionPageState?: CollectionPageState;
}

export function CairoTestApp({
  children,
  driver,
  page,
  scrollElement,
  pageState,
  entityPageState,
  collectionPageState,
}: CairoTestAppProps) {
  let child = children;

  switch (page) {
    case 'wsr':
      child = (
        <Page>
          <Page.Header title="Page Title" />
          <Page.Content>{children}</Page.Content>
        </Page>
      );
      break;
    case 'entity':
      if (!pageState) {
        throw new Error('Page state was not initialized');
      }

      child = (
        <CairoEntityPage state={pageState}>
          <Page.Content>{children}</Page.Content>
        </CairoEntityPage>
      );
      break;
    case 'none':
      child = (
        <div
          ref={scrollElement}
          data-hook="custom-scroll-container"
          style={{ height: '200px', overflow: 'auto' }}
        >
          {child}
        </div>
      );
      break;
    case 'entity-page':
      if (!entityPageState) {
        throw new Error('Page state was not initialized');
      }
      child = (
        <EntityPage state={entityPageState} dataHook={page}>
          {children}
        </EntityPage>
      );
      break;
    default:
      child = (
        <InfiniteScrollPage state={collectionPageState}>
          <Page.Content>{children}</Page.Content>
        </InfiniteScrollPage>
      );
      break;
  }

  return (
    <EssentialsProvider essentials={driver.testContext}>
      <WixStyleReactEnvironmentProvider
        locale={driver.testContext.environment.language}
      >
        <WixPatternsTestProvider>
          <WixPatternsContainerProvider value={driver.container}>
            {child}
          </WixPatternsContainerProvider>
        </WixPatternsTestProvider>
      </WixStyleReactEnvironmentProvider>
    </EssentialsProvider>
  );
}
